import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, TextField, Button, Typography, IconButton, Avatar, Badge, Drawer, CircularProgress, Fade } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { styled, keyframes } from '@mui/material/styles';
import axios from 'axios';

const pulseAnimation = keyframes`
  0%, 100% { transform: scale(0.8); opacity: 0.5; }
  50% { transform: scale(1.2); opacity: 1; }
`;

const ChatButton = styled(Button)(({ theme }) => ({
  position: 'fixed', bottom: '80px', right: '20px', borderRadius: '50%', width: '60px', height: '60px', minWidth: '60px', zIndex: 9999, backgroundColor: '#e17800', '&:hover': { backgroundColor: '#c56700' }
}));

const ChatDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': { width: 320, height: 500, bottom: '80px', right: '20px', left: 'auto', position: 'fixed', borderRadius: '10px 10px 0 0', display: 'flex', flexDirection: 'column' },
}));

const MessageBubble = styled(Box)(({ sender, status }) => ({
  backgroundColor: sender === 'visitor' ? '#e17800' : '#e3f2fd', color: sender === 'visitor' ? '#ffffff' : '#212529', borderRadius: sender === 'visitor' ? '18px 18px 0 18px' : '18px 18px 18px 0', padding: '10px 16px', marginBottom: '8px', maxWidth: '80%', alignSelf: sender === 'visitor' ? 'flex-end' : 'flex-start', opacity: status === 'sending' ? 0.7 : 1
}));

const SystemMessage = styled(Box)(({ theme }) => ({ backgroundColor: '#edf2f7', color: '#4a5568', borderRadius: '10px', padding: '6px 12px', margin: '12px auto', fontSize: '0.85rem', maxWidth: '90%', textAlign: 'center' }));

const ChatHeader = styled(Box)(({ theme }) => ({ backgroundColor: '#e17800', color: '#fff', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }));
const ChatMessages = styled(Box)(({ theme }) => ({ flexGrow: 1, overflow: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', backgroundColor: '#f9f9f9' }));
const ChatFooter = styled(Box)(({ theme }) => ({ padding: '10px', borderTop: '1px solid #eee', backgroundColor: theme.palette.background.paper, display: 'flex' }));
const MessageStatus = styled(Box)(({ theme }) => ({ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: '4px', fontSize: '11px' }));
const Copyright = styled(Box)(({ theme }) => ({ padding: '6px 10px', borderTop: '1px solid #eee', backgroundColor: '#f9f9f9', textAlign: 'center', fontSize: '11px', color: '#888' }));

const LiveChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatSession, setChatSession] = useState({
    visitorToken: localStorage.getItem('virtueExchangeChatToken') || null,
    chatId: null,
    hasActiveChat: false,
    isInitialized: false,
    userInfo: { name: '', email: '' },
    showForm: false,
    unreadCount: 0
  });
  const messagesEndRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const apiUrl = 'https://api.blockfirstcapital.com/api';

  // Initialize chat when component loads
  useEffect(() => {
    if (!chatSession.isInitialized) {
      initializeChat();
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Setup polling for messages
  useEffect(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    if (chatSession.visitorToken) {
      // Set up different polling intervals based on whether chat is open
      const interval = setInterval(() => {
        if (isOpen && chatSession.hasActiveChat) {
          // When chat is open, poll more frequently
          fetchChatHistory();
        } else if (chatSession.hasActiveChat) {
          // When chat is minimized but active, check for unread messages
          checkForNewMessages();
        }
      }, isOpen ? 3000 : 10000); // 3 seconds when open, 10 seconds when closed

      pollingIntervalRef.current = interval;
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [isOpen, chatSession.visitorToken, chatSession.hasActiveChat]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load chat history when opening the chat
  useEffect(() => {
    if (isOpen && chatSession.visitorToken && chatSession.isInitialized) {
      fetchChatHistory();
    }
  }, [isOpen, chatSession.visitorToken, chatSession.isInitialized]);

  const initializeChat = async () => { try { const { data } = await axios.post(`${apiUrl}/live-chat/init`, { visitor_token: chatSession.visitorToken, page_url: window.location.href, page_title: document.title, referrer: document.referrer }); if (data.status === 'success') { const token = data.visitor_token; localStorage.setItem('virtueExchangeChatToken', token); setChatSession(prev => ({ ...prev, visitorToken: token, isInitialized: true })); } } catch (e) { console.error('Init chat failed:', e); } };
  const fetchChatHistory = async () => { if (!chatSession.visitorToken) return; try { setLoading(true); const { data } = await axios.post(`${apiUrl}/live-chat/history`, { visitor_token: chatSession.visitorToken }); if (data.status === 'success') { if (data.has_active_chat) { setMessages(formatMessages(data.messages)); setChatSession(prev => ({ ...prev, chatId: data.chat_id, hasActiveChat: true, showForm: false, unreadCount: 0 })); } else { setChatSession(prev => ({ ...prev, hasActiveChat: false, showForm: true, unreadCount: 0 })); } } } catch (e) { console.error('Fetch history failed:', e); } finally { setLoading(false); } };
  const checkForNewMessages = async () => { if (!chatSession.visitorToken || !chatSession.chatId) return; try { const { data } = await axios.post(`${apiUrl}/live-chat/history`, { visitor_token: chatSession.visitorToken }); if (data.status === 'success' && data.has_active_chat) { setChatSession(prev => ({ ...prev, unreadCount: data.unread_count || 0 })); } } catch (e) { console.error('Check for new messages failed:', e); } };
  const formatMessages = (arr) => (arr || []).map(msg => ({ id: msg.id, text: msg.message, sender: msg.sender_type === 'admin' ? 'Support' : msg.sender_type, time: new Date(msg.created_at), isSystem: msg.sender_type === 'system', status: 'sent' }));
  const startNewChat = async () => { if (!message.trim() || !chatSession.visitorToken) return; const text = message; setMessage(''); setSendingMessage(true); const newMessage = { id: Date.now(), text, sender: 'visitor', time: new Date(), isSystem: false, status: 'sending' }; setMessages([newMessage]); try { const { data } = await axios.post(`${apiUrl}/live-chat/start`, { visitor_token: chatSession.visitorToken, message: text, name: chatSession.userInfo.name, email: chatSession.userInfo.email, page_url: window.location.href, page_title: document.title }); if (data.status === 'success') { setMessages(prev => prev.map(m => m.id === newMessage.id ? { ...m, status: 'sent' } : m)); setTimeout(() => { setMessages(formatMessages(data.messages)); setChatSession(prev => ({ ...prev, chatId: data.chat_id, hasActiveChat: true, showForm: false })); }, 300); } } catch (e) { console.error('Start chat failed:', e); setMessages(prev => prev.map(m => m.id === newMessage.id ? { ...m, status: 'error' } : m)); } finally { setSendingMessage(false); } };
  const sendMessage = async () => { if (!message.trim() || !chatSession.visitorToken || !chatSession.chatId) return; const text = message; setMessage(''); setSendingMessage(true); const newMessage = { id: Date.now(), text, sender: 'visitor', time: new Date(), isSystem: false, status: 'sending' }; setMessages(prev => [...prev, newMessage]); try { await axios.post(`${apiUrl}/live-chat/send`, { visitor_token: chatSession.visitorToken, chat_id: chatSession.chatId, message: text }); setMessages(prev => prev.map(m => m.id === newMessage.id ? { ...m, status: 'sent' } : m)); setTimeout(fetchChatHistory, 300); } catch (e) { console.error('Send failed:', e); setMessages(prev => prev.map(m => m.id === newMessage.id ? { ...m, status: 'error' } : m)); } finally { setSendingMessage(false); } };
  const closeChat = async () => { if (!chatSession.chatId) return; try { await axios.post(`${apiUrl}/live-chat/close`, { visitor_token: chatSession.visitorToken, chat_id: chatSession.chatId }); setMessages(prev => [...prev, { id: Date.now(), text: 'Chat ended by you', sender: 'system', time: new Date(), isSystem: true }]); setChatSession(prev => ({ ...prev, chatId: null, hasActiveChat: false, showForm: true })); } catch (e) { console.error('Close chat failed:', e); } };
  const handleUserInfoChange = (e) => setChatSession(prev => ({ ...prev, userInfo: { ...prev.userInfo, [e.target.name]: e.target.value } }));
  const toggleChat = () => { setIsOpen(!isOpen); if (!isOpen) { setChatSession(prev => ({ ...prev, unreadCount: 0 })); fetchChatHistory(); } };
  const renderMessageStatus = (status, time) => status === 'sending' ? (<Box sx={{ display: 'flex', gap: 0.5 }}><span></span><span></span><span></span></Box>) : status === 'sent' ? (<MessageStatus><Typography variant="caption" sx={{ mr: 0.5, opacity: 0.7 }}>{time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography><DoneIcon sx={{ fontSize: 12, opacity: 0.7 }} /></MessageStatus>) : (<Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5, opacity: 0.7 }}>{time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>);

  return (
    <>
      <ChatButton color="inherit" variant="contained" onClick={toggleChat} aria-label="Open chat">
        <Badge badgeContent={chatSession.unreadCount} color="error">
          <ChatIcon />
        </Badge>
      </ChatButton>

      <ChatDrawer anchor="right" open={isOpen} onClose={toggleChat} variant="persistent">
        <Paper elevation={3} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <ChatHeader>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar src="https://res.cloudinary.com/dqwxmksbe/image/upload/v1762423871/blockfirstcapital-app-icon_gdd8eu.png" alt="Blockfirst Capital" sx={{ width: 32, height: 32, mr: 1 }} />
              <Typography variant="h6">Blockfirst Capital Support</Typography>
            </Box>
            <IconButton size="small" color="inherit" onClick={toggleChat} aria-label="Close chat">
              <CloseIcon />
            </IconButton>
          </ChatHeader>

          <ChatMessages>
            {messages.map((msg) => (
              msg.isSystem ? (
                <Fade in={true} key={msg.id} timeout={500}>
                  <SystemMessage>{msg.text}</SystemMessage>
                </Fade>
              ) : (
                <Fade in={true} key={msg.id} timeout={300}>
                  <Box sx={{ mb: 2 }}>
                    {msg.sender !== 'visitor' && (
                      <Typography variant="caption" display="block" sx={{ textAlign: 'left', mb: 0.5 }}>
                        {msg.sender}
                      </Typography>
                    )}
                    <MessageBubble sender={msg.sender} status={msg.status} sx={{ mb: 0.5 }}>
                      <Typography variant="body2">{msg.text}</Typography>
                    </MessageBubble>
                    {msg.sender === 'visitor' && renderMessageStatus(msg.status, msg.time)}
                  </Box>
                </Fade>
              )
            ))}
            <div ref={messagesEndRef} />
          </ChatMessages>

          <ChatFooter>
            {chatSession.showForm ? (
              <Box sx={{ width: '100%' }}>
                <TextField fullWidth size="small" placeholder="Your Name (optional)" name="name" value={chatSession.userInfo.name} onChange={handleUserInfoChange} sx={{ mb: 1 }} />
                <TextField fullWidth size="small" placeholder="Your Email (optional)" name="email" value={chatSession.userInfo.email} onChange={handleUserInfoChange} sx={{ mb: 1 }} />
                <TextField fullWidth multiline maxRows={3} placeholder="Type your message here..." value={message} onChange={(e) => setMessage(e.target.value)} sx={{ flex: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button variant="contained" style={{ backgroundColor: '#e17800' }} endIcon={sendingMessage ? <CircularProgress size={16} color="inherit" /> : <SendIcon />} onClick={startNewChat} disabled={!message.trim() || loading || sendingMessage}>
                    Start Chat
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                <TextField fullWidth multiline maxRows={3} placeholder="Type your message here..." value={message} onChange={(e) => setMessage(e.target.value)} sx={{ flex: 1 }} />
                <IconButton style={{ color: '#e17800' }} onClick={sendMessage} disabled={!message.trim() || loading || sendingMessage} sx={{ ml: 1 }} aria-label="Send message">
                  {sendingMessage ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                </IconButton>
              </>
            )}
          </ChatFooter>

          <Copyright>
            © {new Date().getFullYear()} Blockfirst Capital. All rights reserved.
          </Copyright>
        </Paper>
      </ChatDrawer>
    </>
  );
};

export default LiveChatWidget;
